/**
 * Task-pane cache for the public tenant config (`GET /tenants/public-config`).
 *
 * The app cannot pick a Firebase project until it knows the tenant for this
 * host, and that lookup goes to the backend. With the backend scaled to zero
 * the lookup waits for a cold start (8-16s measured), during which nothing
 * could be rendered — a white page before the login screen
 * (docs/cloud-cost-optimization, task 12).
 *
 * The answer is public and practically static per host, so the last one is
 * kept in localStorage: boot from the cached copy immediately, re-fetch in the
 * background, and reload only if what matters for sign-in actually changed.
 * Access control is unaffected — every authenticated call is still checked
 * server-side; a cached copy can only let the login screen be drawn.
 *
 * Pure module (no firebase / network imports) so it is unit-testable in node.
 *
 * Mirror of dashboard/src/utils/tenantConfigCache.ts (kept in sync by hand;
 * there is no shared frontend package).
 */

export const TENANT_CONFIG_CACHE_KEY = 'courtyard.tenantPublicConfig.v1';
export const TENANT_CONFIG_RELOAD_GUARD_KEY = 'courtyard.tenantConfigReloadAt';

/** Do not auto-reload more often than this, whatever the backend returns. */
export const RELOAD_GUARD_MS = 60 * 1000;

/** The parts of the tenant config this module needs to reason about. */
export interface TenantConfigShape {
  slug: string;
  status: string;
  firebase_project_id?: string | null;
  firebase_config?: object | null;
}

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface CacheRecord<T> {
  host: string;
  savedAt: number;
  tenant: T;
}

/**
 * The cached tenant for `host`, or null when there is none worth trusting:
 * missing, unparsable, saved for another host, not active, or without a
 * Firebase config. Never throws.
 */
export function readCachedTenantConfig<T extends TenantConfigShape>(
  storage: ReadableStorage | null | undefined,
  host: string,
): T | null {
  if (!storage || !host) return null;
  try {
    const raw = storage.getItem(TENANT_CONFIG_CACHE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw) as Partial<CacheRecord<T>> | null;
    if (!record || record.host !== host) return null;
    const tenant = record.tenant;
    if (!tenant || typeof tenant !== 'object') return null;
    if (typeof tenant.slug !== 'string' || !tenant.slug) return null;
    if (tenant.status !== 'active') return null;
    if (!tenant.firebase_config || typeof tenant.firebase_config !== 'object') return null;
    return tenant;
  } catch {
    return null;
  }
}

export function writeCachedTenantConfig<T extends TenantConfigShape>(
  storage: WritableStorage | null | undefined,
  host: string,
  tenant: T,
  now: number = Date.now(),
): void {
  if (!storage || !host) return;
  try {
    const record: CacheRecord<T> = { host, savedAt: now, tenant };
    storage.setItem(TENANT_CONFIG_CACHE_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable or full — the next load simply waits for the network */
  }
}

export function clearCachedTenantConfig(storage: WritableStorage | null | undefined): void {
  if (!storage) return;
  try {
    storage.removeItem(TENANT_CONFIG_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Stable string of everything that decides how sign-in behaves: which tenant,
 * whether it is active, and the full Firebase web config (project, API key,
 * SAML provider...). Cosmetic fields such as the display name are left out.
 */
export function tenantAuthSignature(tenant: TenantConfigShape): string {
  const config = (tenant.firebase_config ?? {}) as Record<string, unknown>;
  const sortedConfig = Object.keys(config)
    .sort()
    .map((key) => [key, config[key] ?? null]);
  return JSON.stringify([
    tenant.slug,
    tenant.status,
    tenant.firebase_project_id ?? null,
    sortedConfig,
  ]);
}

export type RefreshAction = 'unchanged' | 'reload';

/**
 * Compare the copy the page booted from with the fresh answer.
 *
 * `fresh === null` means the backend no longer knows this host. That, a tenant
 * that is no longer active, or any change to the sign-in relevant fields means
 * the page is running on wrong assumptions and must start over.
 */
export function decideRefreshAction(
  cached: TenantConfigShape,
  fresh: TenantConfigShape | null,
): RefreshAction {
  if (!fresh) return 'reload';
  if (fresh.status !== 'active') return 'reload';
  return tenantAuthSignature(cached) === tenantAuthSignature(fresh) ? 'unchanged' : 'reload';
}

/**
 * Reload-loop guard. Returns true (and records the time) when a reload is
 * allowed now; false when one already happened within `minIntervalMs`.
 */
export function claimReload(
  storage: WritableStorage | null | undefined,
  now: number = Date.now(),
  minIntervalMs: number = RELOAD_GUARD_MS,
): boolean {
  if (!storage) return true;
  try {
    const last = Number(storage.getItem(TENANT_CONFIG_RELOAD_GUARD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < minIntervalMs) {
      return false;
    }
    storage.setItem(TENANT_CONFIG_RELOAD_GUARD_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}
